import { useEffect, useState } from "react";
import { MisterIcon, MisterImage } from "@rtel/mister-icons"

import {
  useTable,
  useColumnOrder,
  Column,
  useSortBy,
  usePagination,
  useRowSelect,
} from "react-table";
import {
  Container,
  Row,
  Col,
  Table as RTable,
  Button,
  Input,
} from "reactstrap";

import { ITable, postOrderOrVisibilityColumnDto } from "./types";
import { BsSortUp, BsSortDownAlt } from "react-icons/bs";
import styles from "./table.module.scss";
import IndeterminateCheckbox from "./IndeterminateCheckbox";
import { useTranslation } from "react-i18next";
import ColumnVisibilityAndOrderMenu from "./ColumnVisibilityAndOrderMenu";
import React from "react";


const sortInitial = {
  id: "1",
  desc: false,
  firstTime: false,
};

const Table = ({
  columns,
  data,
  defaultOrderByKeyFilter,
  showNumberOfRows,
  numberOfRows,
  numberOfRowsTitle,
  multipleRowSelection,
  rowSelection,
  rowSellectionPosition,
  actionRowId,
  debugMode,
  onTableCellClick,
  noDataFoundSubTitle,
  noDataFoundTitle,
  onChangeOrderOrVisibility,
  hiddenColumnsForUser,
  disableColumnsChangeVisibilityForUser,
  ButtonsAreaLeft,
  ButtonsAreaRight,
  setRowSelection,
  isChangeOrderOrVisibilityEnable,
  textPosition,
  maxHeight,
  showAlertOnCellClick,
  minPageSize,
  pagerVisibility,
  footerVisibility,
  striped
}: ITable) => {
  const { t } = useTranslation();
  const [isSortColumnModalVisible, setIsSortColumnModalVisible] = useState(false);
  const [sortColumn, setOrderColumn] = useState(sortInitial);
  const [columnList, setColumnList] = useState<Column<object>[]>(columns);
  const [isSelectedRows, setIsSelectedRows] = useState<boolean>(false);

  const tableHeight = maxHeight === "26rem" ? styles["max-height-23-rem"] : styles["max-height-33-rem"];

  const selectionTitle = t("Selection");

  const pagesToShow = 1; // Number of pages to show on each side of the current page

  const getPageNumbers = (): Array<number | string> => {
    const minPage = Math.max(0, pageIndex - pagesToShow);
    const maxPage = Math.min(pageCount - 1, pageIndex + pagesToShow);
    const range: Array<number | string> = [...Array(maxPage - minPage + 1).keys()].map(i => i + minPage);

    //if (minPage > 0) range.unshift('...');
    //if (minPage > 1) range.unshift(1);
    //if (minPage > 0) range.unshift(0);

    if (maxPage < pageCount - 1) range.push('...');
    if (maxPage < pageCount - 2) range.push(pageCount - 2);
    if (maxPage < pageCount - 1) range.push(pageCount - 1);

    return range;
  };

  const getSelectionColumn = (columns: Column<object>[]) => {
    let newColumns = rowSellectionPosition == "end" ? [...columns] : [];
    if (rowSelection) {
      newColumns.push({
        id: "selection",
        accessor: "selection",
        Header: selectionTitle,
        disableSortBy: true,
        
        Cell: ({ row }) => (
          <IndeterminateCheckbox
            name={"selectBox"}
            {...row.getToggleRowSelectedProps()}
          />
        ),
      });
    }

    if (rowSellectionPosition == "start") newColumns = [...newColumns, ...columns];
    
    return newColumns;
  };

  const getAllHiddenColumns = (columns: Column<object>[]): string[] => {
    return  columns
              .filter((value: any) => value.isVisible === false)
              .map((value: any) => String(value.id));
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    visibleColumns,
    prepareRow,
    setColumnOrder,
    state,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setSortBy,
    setHiddenColumns,
    state: { pageIndex, pageSize },
	  rows,
    selectedFlatRows,
    footerGroups
  } = useTable(
    {
      columns: columnList,
      data,
      autoResetPage: false,
      initialState: {
        pageIndex: 0,
        pageSize: minPageSize,
        hiddenColumns: getAllHiddenColumns(columns),
        columnOrder: columns
          .map((value: any) => String(value.id))
          .sort((value: any) => value.order),
      },
      stateReducer: (newState, action) => {
        let isChecked: boolean;
        Object.keys(newState.selectedRowIds).length > 0
          ? (isChecked = true)
          : (isChecked = false);

        if (
          rowSelection &&
          !multipleRowSelection &&
          action.type === "toggleRowSelected"
        ) {
          newState.selectedRowIds = {
            [action.id]: isChecked,
          };
        }

        setIsSelectedRows(isChecked);
        return newState;
      },
    },
    useSortBy,
    usePagination,
    useColumnOrder,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [...getSelectionColumn(columns)]);
    }
  );

  const toggleSortColumnModalVisibility = () => {
    setIsSortColumnModalVisible(!isSortColumnModalVisible);
  };

  const setColumnOrderAndVisibilityHandler = (columns: Array<Column<object>>) => {
    const sortedByIdColumns = columns.map((column: any) => {
      return column.id != undefined ? column.id : null;
    });

    const hiddenColumns = getAllHiddenColumns(columns)
    setColumnOrder(sortedByIdColumns);
    setColumnList(columns);

    if(onChangeOrderOrVisibility) {
      let newOrderAndVisibilityArray: postOrderOrVisibilityColumnDto [] = [];
      for (const [index, columnName] of sortedByIdColumns.entries()) {
        const isColumnHidden = (hiddenColumns.includes(columnName))
        if (isColumnHidden) {
          newOrderAndVisibilityArray = [...newOrderAndVisibilityArray, {columnName: columnName, columnOrder: index, visible: false}]
        }
        else {
          newOrderAndVisibilityArray = [...newOrderAndVisibilityArray, {columnName: columnName, columnOrder: index, visible: true}]
        }
      }
      if(newOrderAndVisibilityArray.length > 0) {
        onChangeOrderOrVisibility(newOrderAndVisibilityArray);
      }
    }
  };

  const sortColumnHandler = (sortId: string) => {
    let orderType: boolean;
    sortId === sortColumn.id
      ? (orderType = !sortColumn.desc)
      : (orderType = false);

    setSortBy([{ id: sortId, desc: orderType }]);
    setOrderColumn({
      id: sortId,
      desc: orderType,
      firstTime: true,
    });
  };

  const getIndexOfSelectedRows = (selectedRows: Record<string, boolean>): number[] => {
    let tmpSelectedRows: number[] = [];

    if (actionRowId !== "") {
      for (const selectedRowKey in selectedRows) {
        Object.entries(data[+selectedRowKey]).forEach(([key, value]) => {
          if (multipleRowSelection)
            key == actionRowId ? tmpSelectedRows.push(+value) : "";
          else
            key == actionRowId && selectedRows[+selectedRowKey]
              ? tmpSelectedRows.push(+value)
              : "";
        });
      }
    }
    return tmpSelectedRows;
  }
  
   const getSelectedEntireRow = (selectedRowIndexes: Record<string, boolean>): any [] => {
    let selectedRows: any = [];

    Object.entries(selectedRowIndexes).forEach(([key, value]) => {
      if(value === true){
        selectedRows = [...selectedRows, rows[+key].original];
      }
    })
    
    return selectedRows;
  }



  /*useEffect(() =>{
    setHiddenColumns(userDefinedHiddenColumns)
  },[columns])*/

  const isSelectedRowsNotEmpty = (): boolean => {
    return Object.keys(state.selectedRowIds).length !== 0;
  }

  useEffect(() => {
    if( setRowSelection  && isSelectedRowsNotEmpty()) {
      setRowSelection(selectedFlatRows.map(row => row.original));
    }
  }, [selectedFlatRows, setRowSelection]);

  const excludeUserHidenColumnsFromColumnList = (columns: Column<object>[], excludedColumns: string []): Column<object>[] => {
    return columns.filter(column => {
      return !excludedColumns.some(accessor => accessor === column.accessor);
    })
  }

  const getCountOfSelectedRows = (): number => {
    return getSelectedEntireRow(state.selectedRowIds).length;
  }

  return (
    <>
      <Container fluid>
        <Row>
          <Col xs={6} className={`text-start ${styles.buttonsAreaMinHeight} d-flex align-items-center`}>
            {getCountOfSelectedRows() > 0  && 
              <div className="d-inline-block">{getCountOfSelectedRows()} selected</div>
            }
            {ButtonsAreaLeft && 
              <ButtonsAreaLeft />
            }
          </Col>

          <Col xs={6} className={`text-end ${styles.buttonsAreaMinHeight}`}>
            {ButtonsAreaRight && 
              <ButtonsAreaRight />
            }

            {isChangeOrderOrVisibilityEnable && 
             <ColumnVisibilityAndOrderMenu 
                isVisible={isSortColumnModalVisible}
                toggleVisibility={toggleSortColumnModalVisibility}
                columns={excludeUserHidenColumnsFromColumnList(columnList, hiddenColumnsForUser)}
                setColumnOrderAndVisibilityHandler={setColumnOrderAndVisibilityHandler}
                disableColumnsChangeVisibilityForUser={disableColumnsChangeVisibilityForUser}
              />    
            }
          </Col>

          <Col xs={12}>
            <div
              className={`${styles["table-responsive"]} ${tableHeight} ${data.length < 1 ? styles.noDataTableHeader : ''}`}
            >
              <RTable
                {...getTableProps()}
                className="rt-table"
                style={{textAlign: `${textPosition}`}}
                striped={striped}
              >
                <thead className="rt-thead">
                  <>
                    {headerGroups.map((headerGroup) => (
                      <tr
                        {...headerGroup.getHeaderGroupProps()}
                        className="columnList"
                      >
                        <th
                          className={`rt-th rt-resizable-header text-center ${styles.autoIncrementHeader} ${styles["sticky-header"]}`}
                        >
                          AA
                        </th>
                        {visibleColumns.map((column, index) => {
                        
                              return (
                                <th
                                  {...column.getHeaderProps()}
                                  onClick={() => {
                                    if (!column.disableSortBy) {
                                      sortColumnHandler(
                                        String(column.id)
                                      );
                                    }
                                  }}
                                  className={`rt-th rt-resizable-header ${styles["sticky-header"]} ${hiddenColumnsForUser.includes(column.id) && 'd-none'} ${column.id == 'selection' && styles["sellection-header"]}`}
                                >
                                  {column.Header + " "}
                                  <span>
                                    {column.isSorted ? (
                                      column.isSortedDesc ? (
                                        <BsSortUp />
                                      ) : (
                                        <BsSortDownAlt />
                                      )
                                    ) : (
                                      ""
                                    )}
                                  </span>
                                </th>
                              );
                        
                        })}
                      </tr>
                    ))}
                  </>
                </thead>
                  <tbody {...getTableBodyProps()}>
                    {page.map((row, i) => {
                      prepareRow(row);
                      return (
                        <tr
                          {...row.getRowProps()}
                          className={row.isSelected ? styles.selectedRow : ""}
                        >
                          <td className={`${styles.column} text-center`}>{pageIndex * pageSize + i + 1}</td>
                          {row.cells.map((cell) => {
                            return (
                              <td
                                {...cell.getCellProps()}
                                className={`${styles.column} ${hiddenColumnsForUser.includes(cell.column.id) && 'd-none'}`}
                                onClick={() => {
                                  if (showAlertOnCellClick && String(cell.value).length > 53) {
                                    alert(cell.value);
                                  }
                                  console.log(cell)
                                  if (onTableCellClick) {
                                    onTableCellClick(cell.column.id, cell.value, cell.row.index, cell.row.cells);
                                  }
                                }}
                              >                                                                                                                                                                                                                                      
                                {cell.render("Cell")}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                  {footerVisibility && 
                    <tfoot className={styles["sticky-footer"]}>
                      {footerGroups.map(group => (
                        <tr {...group.getFooterGroupProps()}>
                          {group.headers.map((column, index) => (
                            <React.Fragment key={`Footer-${index}`}>
                              {index == 0 && <td></td>}
                              <td {...column.getFooterProps()}>{column.render('Footer')}</td>
                            </React.Fragment>
                          ))}
                        </tr>
                      ))}
                    </tfoot>
                  }
              </RTable>
                
            </div>
            {data.length > 0 && pagerVisibility && (
            <Row className="row pagination mt-3">
              <Col xs={12} md={4}>
                <Input
                  type="select"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                  }}
                  cssModule={{backgroundImage: `url(${<MisterIcon name="ChevronDown" size="sm" />}) !important`}}
                  className={`${styles.paginationItem} ${styles.selectBox}`}
                >
                  {[10, 20, 30, 40, 50].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize} 
                    </option>
                  ))}
                </Input>
                {showNumberOfRows && (
                  numberOfRows! > 0 && <span>από {numberOfRows} {t(numberOfRowsTitle!)}</span>
                )}
              </Col>
              <Col xs={12} md={8} className="text-end">
                <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className={styles.paginationItem}>
                    {'<<'}
                </button>
                <button onClick={() => previousPage()} disabled={!canPreviousPage} className={styles.paginationItem}>
                  {'Prev'}
                </button>
                {getPageNumbers().map((pageNumber, index) => {
                  if (pageNumber === '...') {
                    return <span key={`ellipsis-${index}`} className={styles.paginationElipsis}>{pageNumber}</span>;
                  }
                  return (
                    <button
                      key={`page-${pageNumber}`}
                      onClick={() => gotoPage(+pageNumber)}
                      disabled={pageNumber === pageIndex}
                      className={pageNumber === pageIndex ? `${styles.paginationItem} ${styles.active}` : styles.paginationItem}
                    >
                      {+pageNumber + 1}
                    </button>
                  );
                })}
                <button onClick={() => nextPage()} disabled={!canNextPage} className={styles.paginationItem}>
                  {'Next'}
                </button>
                <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} className={styles.paginationItem}>
                  {'>>'}
                </button>
              </Col>

              
            </Row>
            )}
          </Col>
        </Row>
      </Container>
      
      {data.length < 1 && (
        <Row>
          <Col xs={12} className="text-center" >
            <div className={styles.noDataFoundContainer}>
              <MisterImage name="CallBrowser" />
              <h2>{noDataFoundTitle}</h2>
              <p>{noDataFoundSubTitle}</p>
            </div>
          </Col>
        </Row>
      )}
       
      {debugMode && (
        <pre>
          <code>{JSON.stringify(state, null, 2)}</code>
        </pre>
      )}
    </>
  );
};

export default Table;



Table.defaultProps = {
  showNumberOfRows: false,
  rowSelection: false,
  rowSellectionPosition: "end",
  multipleRowSelection: false,
  actionRowId: "",
  debugMode: false,
  noDataFoundSubTitle: "Select a filter",
  hiddenColumnsForUser: [],
  disableColumnsChangeVisibilityForUser: [],
  isChangeOrderOrVisibilityEnable: true,
  numberOfRowsTitle: "",
  textPosition: "left",
  maxHeight: '26rem',
  showAlertOnCellClick: false,
  minPageSize: 10,
  pagerVisibility: true,
  footerVisibility: false,
  striped: true,
};
